<template>
  <div id="app" class="app-root">
    <Picture
      v-if="preloadProject && useBG && !requireAuth && isMainWindow"
      class="projStyle"
      :style="cssVars"
      :class="gradientColor() ? 'project-color__gradient' : ''"
      extraImageStyle="min-width: 100%; min-height: 100vh; object-fit: cover;"
      :url="preloadProject.backgroundUrl || ''"
      :thumbHash="preloadProject.thumbHash || ''"
      :color="preloadProject.bgColor || ''"
    />
    <div
      v-if="preloadProject && useBG && dimBG && !requireAuth && isMainWindow"
      class="pointer-events-none absolute inset-0 -z-1 bg-project-overlay"
    ></div>
    <template v-if="isMainWindow && routerReady && !requireAuth">
      <CommonHeader></CommonHeader>
      <div class="main-col">
        <Header></Header>
        <router-view />
      </div>
      <Teleport to="body">
        <div class="alerts-holder">
          <div
            class="notify-alert"
            :class="`notify-${a.type}`"
            v-for='a in Object.values(permanentAlerts).filter(a => !!a.message)'
            :key='a.alertId || `alert-${a.message}`'
            @mouseenter="holdAlert({ alertId: a.alertId })">
            <div
              v-if="a.timeout"
              class="alert-countdown-progress"
              :style="{ animationDuration: `${a.timeout}s` }"
            ></div>
            <div class="notify-content flex items-center gap-2">
              <IconCheckCircle v-if="a.type === 'success'" class="w-8 h-8"/>
              <IconExclamationCircle v-if="a.type === 'warning'" class="w-8 h-8"/>
              <IconInformationCircleSolid v-if="a.type === 'info'" class="w-8 h-8"/>
              <span>{{ a.message }}</span>
            </div>
            <i v-if="a.closable" class="tr tr-close" @click="clearAlert({ alertId: a.alertId })"></i>
          </div>
        </div>
      </Teleport>

      <SyncIndicator/>
      <NewYearDecor v-if="currentTheme === 'newyear'" />

      <Transition appear name="slide-fade" type="transition">
        <ActionAlert
          v-if="firstOfActionAlerts"
          :grayButton="firstOfActionAlerts.grayButton ? firstOfActionAlerts.grayButton : null"
          :confirmButton="firstOfActionAlerts.confirmButton ? firstOfActionAlerts.confirmButton : null"
          :actionText="firstOfActionAlerts.actionText ? firstOfActionAlerts.actionText : null"
        />
      </Transition>
      <NoticeTracking />
      <TrackingInfo
        v-if="(activeTrackingTask || showTrackingInfo) && accessTrackingBrowser && !mobileMode"
        class="tracking-information"
      />
    </template>
    <template v-if="isMainWindow && routerReady && requireAuth">
      <div class="relative grid h-screen flex-1 grid-cols-2 gap-20 overflow-hidden bg-bg bg-light-gradient px-16 py-10 text-text max-md:grid-cols-1 max-sm:p-6">
          <template v-if="inApp">
            <div class="absolute inset-x-0 top-0 h-12 [-webkit-app-region:drag]"></div>
            <WindowControls class="absolute top-3 right-4" />
          </template>
          <div class="flex flex-col justify-between gap-12">
            <div class="flex items-center gap-3">
              <img src="/favicon-96-orange.png" class="size-8" alt="" />
              <span class="text-body font-bold">Tracklify</span>
            </div>
            <div class="flex max-w-120 flex-col gap-8">
              <div class="flex flex-col gap-4">
                <span class="font-mono text-label uppercase tracking-widest text-accent">Your workspace is waiting</span>
                <h1 class="m-0 text-5xl leading-none font-bold tracking-tight max-lg:text-4xl max-sm:text-3xl">
                  Pick up right where your team left off.
                </h1>
                <p class="m-0 text-lg leading-7 text-text-muted">
                  Tracklify makes work predictive, productive and aligned
                </p>
              </div>
              <SimpleButton variant="accent" size="lg" bold class="min-h-12! w-fit gap-3! px-6! text-base!" @click="openInBrowser(signInURL)">
                Sign in to Tracklify
                <IconArrowRight class="size-4" />
              </SimpleButton>
            </div>
            <div class="text-body text-text-muted">
              New here?
              <a class="cursor-pointer font-medium text-text transition-colors hover:text-accent" @click="openInBrowser(signUpURL)">Create an account</a>
            </div>
          </div>

          <div
            class="relative flex flex-col max-md:hidden
                   before:absolute before:-inset-y-10 before:-left-10 before:w-px before:bg-line"
            aria-hidden="true"
          >
            <div class="relative flex min-h-0 flex-1 flex-col gap-3 select-none mask-l-from-70% mask-l-to-black/30">
              <div class="grid grid-cols-2 gap-3">
                <div class="flex flex-col gap-3 rounded-tile border border-line bg-input p-5">
                  <span class="text-label text-text-muted">This week</span>
                  <span class="text-3xl leading-none font-bold tabular-nums">32<small class="text-body">h</small> 40<small class="text-body">m</small></span>
                  <div class="h-1.5 rounded-full bg-line"><div class="h-full w-18/25 rounded-full bg-accent"></div></div>
                </div>
                <div class="flex flex-col gap-3 rounded-tile border border-line bg-input p-5">
                  <span class="text-label text-text-muted">On track</span>
                  <span class="text-3xl leading-none font-bold tabular-nums">8 / 9</span>
                  <div class="h-1.5 rounded-full bg-line"><div class="h-full w-22/25 rounded-full bg-green"></div></div>
                </div>
              </div>

              <div class="flex flex-col gap-3 rounded-tile border border-line bg-input p-5">
                <div class="flex justify-between font-mono text-unit tracking-wider text-muted">
                  <span v-for="hour in ['00', '04', '08', '12', '16', '20', '24']" :key="hour">{{ hour }}</span>
                </div>
                <div
                  v-for="(row, i) in SIGNIN_TIMELINE"
                  :key="i"
                  class="relative h-9 border-t border-line"
                >
                  <span
                    v-for="bar in row"
                    :key="bar.left"
                    class="absolute top-1.5 flex h-6 items-center justify-center rounded-chip bg-accent font-mono text-unit font-bold text-on-accent"
                    :style="{ left: `${bar.left}%`, width: `${bar.width}%` }"
                  >{{ bar.label }}</span>
                </div>
              </div>

              <div class="flex min-h-0 flex-1 flex-col gap-3 rounded-tile border border-line bg-input p-5">
                <span class="text-label text-text-muted">This week by day</span>
                <div class="flex min-h-24 flex-1 items-end gap-3">
                  <div v-for="day in SIGNIN_WEEK" :key="day.name" class="flex h-full flex-1 flex-col items-center justify-end gap-2">
                    <span class="font-mono text-label font-bold text-text-muted">{{ day.label }}</span>
                    <span class="w-2/5 rounded-chip bg-accent" :class="!day.today && 'opacity-60'" :style="{ height: `${day.height}%` }"></span>
                    <span class="text-unit font-medium uppercase tracking-wider" :class="day.today ? 'text-accent' : 'text-muted'">{{ day.name }}</span>
                  </div>
                </div>
              </div>

              <div class="relative flex flex-col gap-3 rounded-tile border border-line bg-input p-5">
                <div v-for="width in ['w-2/5', 'w-3/5', 'w-1/3', 'w-1/2', 'w-2/3']" :key="width" class="flex items-center gap-4">
                  <span class="skeleton size-8 shrink-0 rounded-full!"></span>
                  <span class="skeleton h-3.5" :class="width"></span>
                </div>
                <div
                  class="absolute -top-2 left-1/2 z-10 flex -translate-1/2 items-center gap-2 rounded-full border border-line
                         bg-popover-solid px-4 py-2 text-label text-text-muted"
                >
                  <IconLockClosed16Solid class="size-4" />
                  Locked · sign in to view
                </div>
              </div>
            </div>
          </div>
      </div>
    </template>

    <router-view v-if="!isMainWindow" />
  </div>
</template>
<script setup>
import { computed, defineAsyncComponent, onMounted, onUnmounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import Picture from '@/components/elements/Picture';
import Header from '@/components/Header';
import CommonHeader from '@/components/CommonHeader';
import NoticeTracking from '@/components/widgets/NoticeTracking';
import ActionAlert from '@/components/widgets/ActionAlert';
import TrackingInfo from '@/components/widgets/TrackingInfo';
import SimpleButton from '@/components/elements/SimpleButton';
import WindowControls from '@/components/elements/WindowControls.vue';
import SyncIndicator from '@/components/elements/SyncIndicator.vue';
import { currentTheme } from '@/ui/tokens/themes';
import { hasUnfinishedUploads } from '@/utils/uploadQueue';
import config from '@/config';
import trackerEngine from '@/trackingEngine';
import { generateAppUrl, openDesktopApp } from '@/utils';
import { provideNonCriticalRequests } from '@/composables/useNonCriticalRequests';
import { startElectronJumpListSync } from '@/composables/useElectronJumpList';
import { startElectronTrackingProgressSync } from '@/composables/useElectronTrackingProgress';
import { IconCheckCircle, IconExclamationCircle, IconInformationCircleSolid, IconArrowRight, IconLockClosed16Solid } from '@iconify-prerendered/vue-heroicons';

const NewYearDecor = defineAsyncComponent(() => import('@/components/newYear/NewYearDecor.vue'));

const store = useStore();
const route = useRoute();
let stopElectronJumpListSync = null;
let stopElectronTrackingProgressSync = null;

// Vuex state mappings
const routerReady = computed(() => store.state.user.routerReady);
const workspace = computed(() => store.state.user.workspace);
const project = computed(() => store.state.project.project);
const preloadProject = computed(() => store.state.project.preloadProject);
const actionAlertsQueque = computed(() => store.state.project.actionAlertsQueque);
const permanentAlerts = computed(() => store.state.system.permanentAlerts);
const requireAuth = computed(() => store.state.system.requireAuth);
const inApp = !!window.APP_TRON;
const mobileMode = computed(() => store.state.system.mobileMode);
const tabletMode = computed(() => store.state.system.tabletMode);
const tabletSmallMode = computed(() => store.state.system.tabletSmallMode);
const syncing = computed(() => store.state.system.syncing);
const activeTrackingTask = computed(() => store.state.task.activeTrackingTask);
const showTrackingInfo = computed(() => store.state.task.showTrackingInfo);
const accessTrackingBrowser = computed(() => store.getters['user/accessTrackingBrowser']);

// Vuex actions
const initConnection = (payload) => store.dispatch('user/initConnection', payload);
const actionCheckUnhandledNotifics = () => store.dispatch('user/actionCheckUnhandledNotifics');
const actionGetMyRecentTasks = () => store.dispatch('user/actionGetMyRecentTasks');
const setMobileModeAction = (payload) => store.dispatch('system/setMobileModeAction', payload);
const setTabletModeAction = (payload) => store.dispatch('system/setTabletModeAction', payload);
const setTabletSmallModeAction = (payload) => store.dispatch('system/setTabletSmallModeAction', payload);
const stopTracking = (payload) => store.dispatch('task/stopTracking', payload);
const actionGetProjectsList = () => store.dispatch('project/actionGetProjectsList');

// Vuex mutations
const clearAlert = (payload) => store.commit('system/clearAlert', payload);
const holdAlert = (payload) => store.commit('system/holdAlert', payload);

// Provide non-critical requests functionality
const nonCriticalRequestsApi = provideNonCriticalRequests(async () => {
  await actionGetProjectsList();
  await actionCheckUnhandledNotifics();
  await store.dispatch('dayoff/actionLoadTodayDayoffs');
});

// Computed properties
const signInURL = computed(() => {
  if (window.APP_TRON) {
    return `${config.getSPADomain()}/run-app/`;
  }
  return `${config.getSSRDomain()}/login/`;
});

const isMainWindow = computed(() => {
  const routes = ['#/approveWindow', '#/notifyWindow', '#/notifyDownTime', '#/resumeWindow', '#/taskNotifyWindow', '#/stillHereWindow'];
  const currentHashLocation = window.location.hash;
  return !routes.includes(currentHashLocation);
});


const cssVars = computed(() => {
  return {
    '--preload-project': preloadProject.value.bgColor,
    '--preload-project-darken': lightenDarkenColor(preloadProject.value.bgColor, -50),
  };
});

const useBG = computed(() => {
  return ['project', 'projectTask', 'myTasks'].includes(route.name);
});

const dimBG = computed(() => ['project', 'projectTask', 'myTasks'].includes(route.name));

const firstOfActionAlerts = computed(() => {
  return actionAlertsQueque.value && actionAlertsQueque.value.length && actionAlertsQueque.value[0];
});

// Methods
const signUpURL = computed(() => `${config.getSSRDomain()}/starting/`);

const SIGNIN_TIMELINE = [
  [{ left: 4, width: 9, label: '1h' }, { left: 34, width: 22, label: '2h 6m' }, { left: 60, width: 18, label: '1h 36m' }],
  [{ left: 20, width: 30, label: '3h 37m' }, { left: 66, width: 14, label: '58m' }],
  [{ left: 10, width: 16, label: '1h 12m' }, { left: 44, width: 38, label: '4h 20m' }],
];

const SIGNIN_WEEK = [
  { name: 'Mon', label: '7h', height: 49 },
  { name: 'Tue', label: '8h', height: 56 },
  { name: 'Wed', label: '6h', height: 42 },
  { name: 'Thu', label: '7h', height: 50 },
  { name: 'Fri', label: '4h', height: 29, today: true },
  { name: 'Sat', label: '0h', height: 3 },
  { name: 'Sun', label: '0h', height: 3 },
];

const openInBrowser = (url) => {
  if (window.APP_TRON) {
    window.APP_TRON.ipcRenderer.send('openExternal', url);
  } else {
    document.location.href = url;
  }
};

const mountedAction = async () => {
  // Non-critical requests are now set up via provide/inject pattern
  
  if (isMainWindow.value && !requireAuth.value) {
    trackerEngine.initializeTracking();
    await initConnection({});

   
  }
};

const showInApp = async () => {
  const url = generateAppUrl('open', { path: window.location.pathname });
  await openDesktopApp(url);
};

const resize = () => {
  const isMobile = window.innerWidth <= 600;
  const isTablet = window.innerWidth > 1024 && window.innerWidth <= 1200;
  const isTabletSmall = window.innerWidth > 600 && window.innerWidth <= 1024;
  if (mobileMode.value !== isMobile) {
    setMobileModeAction(isMobile);
  }
  if (tabletMode.value !== isTablet) {
    setTabletModeAction(isTablet);
  }
  if (tabletSmallMode.value !== isTabletSmall) {
    setTabletSmallModeAction(isTabletSmall);
  }
};

const lightenDarkenColor = (col, amt) => {
  if (!col || !amt) return;
  const num = parseInt(col.slice(1), 16);
  // eslint-disable-next-line no-bitwise
  const r = (num >> 16) + amt;
  // eslint-disable-next-line no-bitwise
  const b = ((num >> 8) & 0x00FF) + amt;
  // eslint-disable-next-line no-bitwise
  const g = (num & 0x0000FF) + amt;
  // eslint-disable-next-line no-bitwise
  const newColor = g | (b << 8) | (r << 16);
  // eslint-disable-next-line consistent-return
  return `#${newColor.toString(16)}`;
};

const gradientColor = () => {
  if (!mobileMode.value && preloadProject.value.backgroundUrl) {
    return true;
  }
  return false;
};

// the startup loader covers the page until these styles let the app cover it; left in place, its endless spin
// would make the window draw every frame. Offline the sheet's font import fails, and with it the import, so the
// loader goes either way. A hot reload of this component mounts it again, with the loader gone
const removeStartupLoaderOnceStyled = styles => styles
  .catch(e => console.error('App styles did not load fully', e))
  .finally(() => document.getElementById('waiter')?.remove());

// Lifecycle hooks
onMounted(async () => {
  window.addEventListener('resize', resize);
  stopElectronJumpListSync = startElectronJumpListSync(store);
  stopElectronTrackingProgressSync = startElectronTrackingProgressSync(store);

  if (isMainWindow.value) {
    removeStartupLoaderOnceStyled(import('@/assets/style.scss'));
    window.onbeforeunload = () => {
      if (syncing.value || activeTrackingTask.value) {
        stopTracking({
          trigEvent: 'pause',
          manual: true,
          causedBy: 'windowClose',
        });
        // return true;
      }
      // the browser asks before leaving: closing would cut the files still uploading. The desktop app
      // refuses to close on this answer without asking, so it is left out there.
      if (hasUnfinishedUploads.value && !window.APP_TRON) {
        return true;
      }
      return undefined;
    };
    await mountedAction();
    await actionGetMyRecentTasks();
  } else {
    removeStartupLoaderOnceStyled(import('@/assets/popupWindowsStyle.scss'));
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', resize);
  if (stopElectronJumpListSync) {
    stopElectronJumpListSync();
    stopElectronJumpListSync = null;
  }
  if (stopElectronTrackingProgressSync) {
    stopElectronTrackingProgressSync();
    stopElectronTrackingProgressSync = null;
  }
});

// Watchers
watch(requireAuth, () => {
  mountedAction();
});
</script>

<style lang="scss">
body:not([data-locale=ru]) {
  ._hj_feedback_container {
    display: none;
  }
}
</style>
<style lang="scss" scoped>
.slide-fade-enter-active, .slide-fade-leave-active {
  transition: all 0.3s ease-out;
}

.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

.main-col {
  display: flex;
  flex-direction: column;
  width: calc(100vw - var(--left-rail-width));
  height: 100%;
  @media screen and (max-width: 600px) {
    width: 100vw;
    flex: 1;
    min-height: 0;
  }
}

.projStyle[class] {
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  z-index: -1;
  border-radius: 0;
}
i {
  cursor: pointer;
}
.project-color {
  &__gradient {
    background: linear-gradient(135deg, var(--preload-project), var(--preload-project-darken));
  }
}

.alerts-holder {
  position: fixed;
  right: 40px;
  bottom: 100px;
  z-index: var(--z-toast);
  margin-top: 80px; // room for close
}
.notify-alert {
  margin-bottom: 20px;
  &.notify-warning {
    background: color-mix(in srgb, var(--red) 55%, transparent);
    border-left: 4px solid rgb(180, 0, 0);
    box-shadow: 1px 1px 8px var(--shadow-color);
    font-weight: 500;
    color: #fff;
  }
  &.notify-success {
    background: color-mix(in srgb, var(--green) 55%, transparent);
    border-left: 4px solid rgb(0, 151, 33);
    box-shadow: 1px 1px 8px var(--shadow-color);
    font-weight: 500;
    color: #fff;
  }
  &.notify-info {
    background: color-mix(in srgb, var(--blue) 55%, transparent);
    border-left: 4px solid rgb(0, 100, 180);
    box-shadow: 1px 1px 8px var(--shadow-color);
    font-weight: 500;
    color: #fff;
  }
  position: relative;
  z-index: 1;
  padding: 14px 20px;
  border-radius: 5px;
  max-width: 240px;
  overflow: hidden;
  &:hover {
    opacity: 0.9;
  }
  .notify-content {
    position: relative;
    z-index: 1;
  }
  .tr-close {
    position: absolute;
    font-size: 10px;
    top: 10px;
    right: 10px;
    z-index: 2;
    &:hover {
      opacity: 0.8;
    }
  }
}

.alert-countdown-progress {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 100%;
  background: rgba(220, 224, 232, 0.5);
  z-index: 0;
  animation-name: alert-countdown-progress-shrink;
  animation-timing-function: linear;
  animation-fill-mode: forwards;
}

@keyframes alert-countdown-progress-shrink {
  from {
    width: 100%;
    opacity: 0.45;
  }
  to {
    width: 0%;
    opacity: 0;
  }
}



.app-version {
  position: fixed;
  z-index: var(--z-toast);
  right: 0;
  bottom: 0;
  background: var(--bg2);
  padding: 2px;
  font-size: 8px;
  border-radius: 2px 0 0 0;
}

#app {
  display: flex;
  flex-direction: row;
  @media screen and (max-width: 600px) {
    background: var(--bg) !important;
    flex-direction: column;
  }
}
// .app-tracking-message-container {
//   height: 100%;
//   width: 100%;
//   position: absolute;
//   z-index: 1000000;
//   display: flex;
//   justify-content: center;
//   align-items: center;

//   .dialog {
//     display: flex;
//     flex-direction: column;
//     justify-content: center;
//     align-items: center;
//     width: 700px;
//     height: 150px;
//     background: var(--bg2);
//     border-radius: 15px;
//     color: var(--text);
//     text-align: center;
//     font-size: 24px;
//     padding: 15px;
//     box-shadow: 1px 1px 6px var(--shadow-color);


//     .button-container {
//       font-size: 18px;
//       display: flex;
//       align-content: flex-start;
//       justify-content: center;
//       gap: 45px;
//       margin-top: 30px;
//     }
//   }
//   &::after {
//     content: '';
//     position: absolute;
//     z-index: -1;
//     top: 0;
//     left: 0;
//     right: 0;
//     bottom: 0;
//     backdrop-filter: blur(15px);
//   }
// }
:deep(.simple-button-text), :deep(.red-simple-button-text) {
  font-size: 12px;
  line-height: 16px;
}
</style>

<style lang="scss">
// here I defined global styles which should not be scaled with vw

$recycleItemWidth: 160px;
$recycleItemMargin: 20px;
$desktopItems: 4;
$mobileItems: 2;

.vue-recycle-scroller.screenshots-scroller {
  width: 100%;
  overflow-x: auto;

  width: calc(($recycleItemWidth + $recycleItemMargin) * $desktopItems + $recycleItemMargin) + 16px; //last margin - some scroll compensation to prevent overflowx blinks/jumps
  .vue-recycle-scroller__item-wrapper {
    min-width: calc(($recycleItemWidth + $recycleItemMargin) * $desktopItems + $recycleItemMargin);
  }
  .vue-recycle-scroller__item-view {
    padding: $recycleItemMargin / 2;
    box-sizing: border-box;
  }

  &.mobile-mode {
    width: calc(($recycleItemWidth + $recycleItemMargin) * $mobileItems + $recycleItemMargin);
    .vue-recycle-scroller__item-wrapper {
      min-width: calc(($recycleItemWidth + $recycleItemMargin) * $mobileItems + $recycleItemMargin);
    }
  }

  .skeleton-wrapper  {
    display: flex;
    flex-direction: row;

    .screenshot-loader {
      margin: $recycleItemMargin / 2;
      width: $recycleItemWidth;
      .screenshot-loader__info {
        display: flex;
        justify-content: space-between;
        margin-top: 10px;
      }

    }
  }
}

</style>
